import { useState, useEffect } from "react";
import { api } from "../api";
import { Loader2, Plug, CheckCircle2, AlertTriangle, Trash2, ExternalLink, Search, RefreshCw } from "lucide-react";

interface Connector {
  id: number;
  name: string;
  type: string;
  config: string;
  enabled: boolean;
}

interface ComposioToolkit {
  name: string;
  slug: string;
  description?: string;
  meta?: {
    description?: string;
    logo?: string;
  };
}

interface ComposioConnection {
  id?: string;
  toolkit?: string;
  status?: string;
}

export default function Tools() {
  const [connectors, setConnectors] = useState<Connector[]>([]);
  const [apps, setApps] = useState<ComposioToolkit[]>([]);
  const [connections, setConnections] = useState<ComposioConnection[]>([]);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState<string | null>(null);
  const [message, setMessage] = useState<{ type: "success" | "error"; text: string } | null>(null);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState<{ configured?: boolean; error?: string; toolkits_available?: number } | null>(null);

  useEffect(() => {
    loadAll();
  }, []);

  const loadAll = async () => {
    setLoading(true);
    setMessage(null);
    try {
      const [c, s, a, conn] = await Promise.all([
        api.listConnectors(),
        api.getComposioStatus().catch((e) => ({ configured: false, error: e.message || "Status check failed" })),
        api.listComposioApps().catch(() => []),
        api.listComposioConnections().catch(() => []),
      ]);
      setConnectors(c.connectors || []);
      setStatus(s);
      setApps(a.items || a.apps || a || []);
      setConnections(conn.items || conn.connections || conn || []);
    } catch (e) {
      setMessage({ type: "error", text: e instanceof Error ? e.message : "Could not load tools" });
    } finally {
      setLoading(false);
    }
  };

  const connectApp = async (app: ComposioToolkit) => {
    setConnecting(app.slug);
    setMessage(null);
    try {
      const redirectUrl = `${window.location.origin}/settings?tab=tools`;
      const data = await api.connectComposioApp(app.slug, redirectUrl);
      await api.createConnector({
        name: app.name,
        type: "composio",
        config: { app_key: app.slug, connection_id: data.id || data.connectionId || data.connection_id || "" },
        enabled: true,
      });
      await loadAll();
      const url = data.redirectUrl || data.redirect_url || data.redirectUri;
      if (url) {
        window.open(url, "_blank");
        setMessage({ type: "success", text: `${app.name} connector added. Complete auth in the opened window.` });
      } else {
        setMessage({ type: "success", text: `${app.name} connected.` });
      }
    } catch (e) {
      const err = e instanceof Error ? e.message : "Connection failed";
      if (err.includes("COMPOSIO_API_KEY") || err.includes("not configured")) {
        setStatus({ configured: false, error: "COMPOSIO_API_KEY not configured" });
      }
      setMessage({ type: "error", text: err });
    } finally {
      setConnecting(null);
    }
  };

  const deleteConnector = async (id: number) => {
    if (!confirm("Remove this connector?")) return;
    try {
      await api.deleteConnector(id);
      await loadAll();
      setMessage({ type: "success", text: "Connector removed." });
    } catch (e) {
      setMessage({ type: "error", text: e instanceof Error ? e.message : "Delete failed" });
    }
  };

  const connectorAppKeys = new Set(
    connectors
      .filter((c) => c.enabled)
      .map((c) => {
        try {
          return JSON.parse(c.config || "{}").app_key;
        } catch {
          return "";
        }
      })
      .filter(Boolean)
  );

  const activeConnectionKeys = new Set(
    connections
      .filter((c) => (c.status || "").toLowerCase() === "active")
      .map((c) => c.toolkit || "")
      .filter(Boolean)
  );

  const isConnected = (slug: string) => connectorAppKeys.has(slug) && activeConnectionKeys.has(slug);

  const filteredApps = apps.filter(
    (a) =>
      a.name?.toLowerCase().includes(search.toLowerCase()) ||
      a.slug?.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-trofa-700" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {message && (
        <div className={`flex items-center gap-2 rounded-lg px-4 py-3 text-sm ${message.type === "success" ? "bg-green-50 text-green-700 border border-green-200" : "bg-red-50 text-red-700 border border-red-200"}`}>
          {message.type === "success" ? <CheckCircle2 className="w-5 h-5" /> : <AlertTriangle className="w-5 h-5" />}
          {message.text}
        </div>
      )}

      {status?.configured === false && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-sm text-amber-800 flex items-start gap-2">
          <AlertTriangle className="w-5 h-5 shrink-0" />
          <div>
            <p className="font-medium">Composio is not configured</p>
            <p className="mt-1">Ask an admin to set <code className="bg-amber-100 px-1 rounded">COMPOSIO_API_KEY</code> in <code className="bg-amber-100 px-1 rounded">/root/trofa-dashboard/.env</code> and restart the backend container.</p>
          </div>
        </div>
      )}

      {status?.configured === true && status.error && (
        <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700 flex items-center gap-2">
          <AlertTriangle className="w-5 h-5" />
          Composio API key is set but the API returned an error: {status.error}
        </div>
      )}

      <section className="bg-white rounded-xl border border-slate-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
            <Plug className="w-5 h-5 text-trofa-700" /> Connected Tools
          </h2>
          <button
            onClick={loadAll}
            className="inline-flex items-center gap-1 text-sm text-trofa-700 hover:bg-trofa-50 px-2 py-1 rounded-lg transition-colors"
          >
            <RefreshCw className="w-4 h-4" /> Refresh
          </button>
        </div>
        {connectors.length === 0 ? (
          <p className="text-sm text-slate-400">No tools connected yet. Connect an app below.</p>
        ) : (
          <div className="space-y-2">
            {connectors.map((c) => {
              const appKey = (() => {
                try { return JSON.parse(c.config || "{}").app_key; } catch { return ""; }
              })();
              const active = appKey ? activeConnectionKeys.has(appKey) : false;
              return (
                <div key={c.id} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
                  <div className="flex items-center gap-3">
                    {appKey && (
                      <img
                        src={`https://logos.composio.dev/app-Logos/${appKey}.png`}
                        alt=""
                        className="w-6 h-6 rounded"
                        onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }}
                      />
                    )}
                    <div>
                      <p className="font-medium text-slate-800">{c.name}</p>
                      <p className="text-xs text-slate-500 flex items-center gap-1">
                        {active ? <><CheckCircle2 className="w-3 h-3 text-green-600" /> Active</> : <><AlertTriangle className="w-3 h-3 text-amber-600" /> {c.enabled ? "Auth pending" : "Disabled"}</>}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => deleteConnector(c.id)}
                    className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    title="Remove"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </section>

      <section className="bg-white rounded-xl border border-slate-200 p-6">
        <h2 className="text-lg font-semibold text-slate-900 mb-4">Available Apps</h2>
        <div className="relative mb-4">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="text"
            placeholder="Search apps..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full border border-slate-300 rounded-lg pl-9 pr-3 py-2 focus:ring-2 focus:ring-trofa-700"
          />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {filteredApps.slice(0, 60).map((app) => {
            const connected = isConnected(app.slug);
            const logo = app.meta?.logo || `https://logos.composio.dev/app-Logos/${app.slug}.png`;
            return (
              <div
                key={app.slug}
                className="flex items-center gap-3 p-3 rounded-lg border border-slate-200 hover:border-trofa-400 transition-colors"
              >
                <img
                  src={logo}
                  alt=""
                  className="w-8 h-8 rounded object-contain"
                  onError={(e) => { (e.target as HTMLImageElement).src = "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7"; }}
                />
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-slate-800 text-sm truncate">{app.name}</p>
                  <p className="text-xs text-slate-500 truncate">{app.description || app.meta?.description || app.slug}</p>
                </div>
                <button
                  onClick={() => connectApp(app)}
                  disabled={connected || !!connecting || status?.configured === false}
                  className={`shrink-0 inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                    connected
                      ? "bg-green-50 text-green-700 border border-green-200 cursor-default"
                      : "bg-trofa-700 text-white hover:bg-trofa-800 disabled:opacity-50 disabled:cursor-not-allowed"
                  }`}
                >
                  {connecting === app.slug ? (
                    <>
                      <Loader2 className="w-3 h-3 animate-spin" />
                      Connecting
                    </>
                  ) : connected ? (
                    <>
                      <CheckCircle2 className="w-3 h-3" />
                      Done
                    </>
                  ) : (
                    <>
                      <ExternalLink className="w-3 h-3" />
                      Connect
                    </>
                  )}
                </button>
              </div>
            );
          })}
        </div>
        {filteredApps.length === 0 && !loading && (
          <p className="text-sm text-slate-400 mt-4">No apps found.</p>
        )}
      </section>
    </div>
  );
}
